import { useMutation, useQuery } from '@tanstack/react-query';
import { AuthorizationManager } from '@globus/sdk/dist/esm/core/authorization';
import { transfer, errors as globusErrors } from '@globus/sdk';
import { useGlobusAuth } from '@globus/react-auth-context';
import { FileListDocument } from '@globus/sdk/dist/esm/services/transfer/service/file-operations';

async function fetchCollections(
    authorization: AuthorizationManager | undefined,
    options: any
) {
    const response = await transfer.endpointSearch(options,
        { manager: authorization }
    );
    return response.json();
}

export enum LS_ERROR {
  "INVALID_PERMISSION" = "INVALID_PERMISSION",
  "REAUTH_REQUIRED" = "REAUTH_REQUIRED"
}

export type TGlobusError = {
  code: string;
  message: string;
  resource: string;
};

export class GlobusError extends Error {
    constructor(
    readonly code: string,
    readonly message: string,
    readonly resource?: string
    ) {
        super(message);
        Object.setPrototypeOf(this, GlobusError.prototype);
    }
}

/**
 * Detects the two well-known Globus "session/consent" error shapes:
 * - AuthorizationRequirementsError (a.k.a. GARE - Globus Auth Requirements Error), which carries
 *   an `authorization_parameters` object (session expired / additional identity or MFA required).
 * - ConsentRequiredError, which carries a `required_scopes` array (missing/insufficient consent).
 *
 * Both mean the same thing to the end user: they need to re-authenticate with Globus before the
 * operation can succeed. This is what surfaces in the Globus Activity log as a "permission denied" /
 * "500 Path not allowed" fault for new or renewed identities whose session hasn't yet satisfied the
 * collection's session policy - it is not an actual file permission problem.
 */
export function isReauthRequiredError(response: unknown): boolean {
    return (
        globusErrors.isAuthorizationRequirementsError(response) ||
        globusErrors.isConsentRequiredError(response)
    );
}

export type Collection = Awaited<ReturnType<typeof fetchCollections>>;

export function useGetCollections(queryKey: string, options: any) {
    const { authorization, isAuthenticated } = useGlobusAuth();
    return useQuery({
        enabled: isAuthenticated,
        queryKey: ["globus_collections", queryKey],
        queryFn: () => fetchCollections(authorization, options),
    });
}

export async function ls(
    authorization: AuthorizationManager | undefined,
    collection: string,
    path?: string,
    options: Parameters<typeof transfer.fileOperations.ls>[1] = {},
) {
    const response = await transfer.fileOperations.ls(
        collection,
        {
            query: {
                path: path ?? undefined,
                ...(options?.query || {}),
            },
        },
        {
            manager: authorization,
        },
    );

    if (response.ok) {
        return await response.json() as FileListDocument;
    } else {
        const error:any = await response.json();
        if (response.status === 401) {
            throw new GlobusError("UNAUTHORIZED", LS_ERROR.INVALID_PERMISSION)
        } else if (isReauthRequiredError(error)) {
            throw new GlobusError("REAUTH_REQUIRED", LS_ERROR.REAUTH_REQUIRED)
        } else {
            throw new GlobusError(error?.code, error.message, error.resource ?? "")
        }
    }
}

async function mkdir(
    authorization: AuthorizationManager | undefined,
    collection: string,
    path: string,
) {
    const response = await transfer.fileOperations.mkdir(
        collection,
        {
            payload: {
                path: path
            }
        },
        {
            manager: authorization,
        },
    );
    if (response.ok) {
        return await response.json();
    }
    let error: any;
    try {
        error = await response.json();
    } catch {
        // do nothing
    }
    if (isReauthRequiredError(error)) {
        return Promise.reject(new GlobusError("REAUTH_REQUIRED", LS_ERROR.REAUTH_REQUIRED));
    }
    return Promise.reject(error);
}

export function useMkdir() {
    const { authorization } = useGlobusAuth();
    return useMutation({
        mutationFn: ({ collection, path }: any) => mkdir(authorization, collection, path),
        onSuccess: () => {
        },
    });
}